import { useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import puntos1 from '../../../../assets/puntos1.webp';
import { ACHIEVEMENTS } from '../../data/achievements';
import SOLO_PLAY_REWARDS from '../../data/shared/soloPlayRewards.json';
import { fetchScoringGuide } from '../../infrastructure/scoringGuideApi';

const DEFAULT_GUIDE = {
  scoring: {
    challengeWinner: 100,
    challengeLoser: 50,
    achievements: {},
  },
  hiddenPoints: { pointsPerFind: 10 },
};

const pointsLabel = (points) => `+${Number(points || 0).toLocaleString('es-MX')}`;

function PointsRule({ points, title, description, frequency, children }) {
  return (
    <li className="border border-[#7EC54A]/25 bg-black/25 p-3">
      <div className="grid grid-cols-[minmax(5rem,.7fr)_minmax(0,1.8fr)] gap-3">
        <div>
          <p className="text-[9px] font-black uppercase tracking-[.14em] text-[#C5E6A0]">Puntos</p>
          <p className="mt-1 font-display text-xl leading-none text-[#F7E04B]">{points}</p>
        </div>
        <div>
          <p className="text-xs font-black uppercase tracking-wide text-white">{title}</p>
          <p className="mt-1 text-xs leading-5 text-white/80">{description}</p>
          <p className="mt-2 text-[10px] leading-4 text-[#C5E6A0]">
            <span className="font-black uppercase tracking-wider">Cuántas veces: </span>{frequency}
          </p>
        </div>
      </div>
      {children}
    </li>
  );
}

// Botón de texto sutil para reabrir las instrucciones desde el mapa.
// Sin envolvente: sin borde, sin fondo, sin sombra. Solo texto pequeño.
export default function MapInstructions({ buttonClassName = 'flex' }) {
  const [isOpen, setIsOpen] = useState(false);
  const [guide, setGuide] = useState(DEFAULT_GUIDE);

  const close = useCallback(() => setIsOpen(false), []);

  useEffect(() => {
    if (!isOpen) return undefined;
    const handleKey = (event) => {
      if (event.key === 'Escape') close();
    };
    window.addEventListener('keydown', handleKey);
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', handleKey);
      document.body.style.overflow = previousOverflow;
    };
  }, [isOpen, close]);

  useEffect(() => {
    if (!isOpen) return undefined;
    let cancelled = false;
    fetchScoringGuide()
      .then((nextGuide) => { if (!cancelled) setGuide(nextGuide); })
      .catch(() => { /* Los valores predeterminados mantienen la ayuda disponible sin red. */ });
    return () => { cancelled = true; };
  }, [isOpen]);

  const achievementPoints = ACHIEVEMENTS.map(
    (achievement) => Number(guide.scoring.achievements?.[achievement.id] ?? 100),
  );
  const distinctAchievementPoints = [...new Set(achievementPoints)];
  const achievementPointsLabel = distinctAchievementPoints.length === 1
    ? `${pointsLabel(distinctAchievementPoints[0])} cada una`
    : `${pointsLabel(Math.min(...achievementPoints))} a ${pointsLabel(Math.max(...achievementPoints))}`;

  return (
    <>
      <button
        type="button"
        onClick={() => setIsOpen(true)}
        aria-haspopup="dialog"
        aria-expanded={isOpen}
        title="Ver cómo jugar"
        className={`${buttonClassName} min-h-[44px] shrink-0 items-center bg-transparent px-1 text-[11px] font-medium normal-case tracking-normal text-white/60 underline decoration-white/25 underline-offset-4 transition-colors hover:text-white hover:decoration-white/60 active:text-white/90`}
      >
        ¿Cómo jugar?
      </button>

      {isOpen && typeof document !== 'undefined' && createPortal(
        <div
          className="fixed inset-0 z-[95] flex overflow-hidden bg-black/85 p-4 pb-[max(1.5rem,env(safe-area-inset-bottom))] pt-[max(5.5rem,env(safe-area-inset-top))] backdrop-blur-sm"
          role="dialog"
          aria-modal="true"
          aria-label="Instrucciones de juego"
          onClick={close}
        >
          <div
            className="m-auto flex max-h-full w-full max-w-lg flex-col overflow-hidden border border-[#F7E04B]/40 bg-black/95 shadow-[8px_10px_0_rgba(0,0,0,.55)]"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="flex shrink-0 items-start justify-between gap-4 border-b border-white/10 p-4">
              <div>
                <p className="text-[10px] font-black uppercase tracking-[.2em] text-[#F7E04B]">Instrucciones</p>
                <h2 className="mt-1 font-display text-2xl uppercase leading-none text-white">Cómo maldecir Toluca</h2>
              </div>
              <button
                type="button"
                onClick={close}
                autoFocus
                className="shrink-0 border border-white/20 bg-white/5 px-3 py-2 text-xs font-black uppercase text-white"
              >
                Cerrar
              </button>
            </div>

            <div className="min-h-0 overflow-y-auto p-4 [-webkit-overflow-scrolling:touch]">

            <ol className="grid gap-2">
              <li className="flex gap-3 border border-[#7EC54A]/25 bg-black/25 p-3">
                <span className="flex h-7 w-7 shrink-0 items-center justify-center bg-[#F7E04B] font-display text-sm leading-none text-[#0A1A0F]">1</span>
                <p className="text-xs leading-5 text-white"><span className="font-black text-[#F7E04B]">Sigue el ritmo.</span> Las notas caen por dos carriles hacia el receptor al beat de la música.</p>
              </li>
              <li className="flex gap-3 border border-[#7EC54A]/25 bg-black/25 p-3">
                <span className="flex h-7 w-7 shrink-0 items-center justify-center bg-[#F7E04B] font-display text-sm leading-none text-[#0A1A0F]">2</span>
                <p className="text-xs leading-5 text-white"><span className="font-black text-[#F7E04B]">Toca a tiempo.</span> Pulsa izquierda <span className="rounded bg-white/10 px-1 font-mono text-[11px]">←</span> o derecha <span className="rounded bg-white/10 px-1 font-mono text-[11px]">→</span> / toca el lado correspondiente cuando la nota llegue.</p>
              </li>
              <li className="flex gap-3 border border-[#7EC54A]/25 bg-black/25 p-3">
                <span className="flex h-7 w-7 shrink-0 items-center justify-center bg-[#F7E04B] font-display text-sm leading-none text-[#0A1A0F]">3</span>
                <p className="text-xs leading-5 text-white"><span className="font-black text-[#F7E04B]">Mantén pulsado.</span> En notas largas, no sueltes hasta que termine la estela. Si sueltas antes, pierdes la racha.</p>
              </li>
              <li className="flex gap-3 border border-[#7EC54A]/25 bg-black/25 p-3">
                <span className="flex h-7 w-7 shrink-0 items-center justify-center bg-[#F7E04B] font-display text-sm leading-none text-[#0A1A0F]">4</span>
                <p className="text-xs leading-5 text-white"><span className="font-black text-[#F7E04B]">Maldice y multiplica.</span> Cada fallo te quita tiempo. Mantén la racha para activar <span className="font-black">x2 · x3 · x4</span>.</p>
              </li>
              <li className="flex gap-3 border border-[#7EC54A]/25 bg-black/25 p-3">
                <span className="flex h-7 w-7 shrink-0 items-center justify-center bg-[#F7E04B] font-display text-sm leading-none text-[#0A1A0F]">5</span>
                <p className="text-xs leading-5 text-white"><span className="font-black text-[#F7E04B]">Maldice a tus amigos.</span> Lanza un reto con un amigo y gana más puntos.</p>
              </li>
            </ol>

            <div className="mt-3 border border-[#C9A8FF]/35 bg-[#1A1033]/70 p-3">
              <p className="flex items-center gap-2 text-[10px] font-black uppercase tracking-[.18em] text-[#C9A8FF]"><span className="flex h-5 w-5 items-center justify-center bg-[#C9A8FF] text-[10px] leading-none text-[#0A1A0F]">◈</span> MAPA · Maldice tu territorio</p>
              <ol className="mt-3 grid gap-2">
                <li className="flex gap-3">
                  <span className="flex h-6 w-6 shrink-0 items-center justify-center bg-[#C9A8FF] font-display text-xs leading-none text-[#0A1A0F]">1</span>
                  <p className="text-xs leading-5 text-white"><span className="font-black text-[#C9A8FF]">Posición maldita.</span> El mapa te posicionará en la zona más cercana a ti para comenzar la maldición.</p>
                </li>
                <li className="flex gap-3">
                  <span className="flex h-6 w-6 shrink-0 items-center justify-center bg-[#C9A8FF] font-display text-xs leading-none text-[#0A1A0F]">2</span>
                  <p className="text-xs leading-5 text-white"><span className="font-black text-[#C9A8FF]">Puntos ocultos.</span> <img src={puntos1} alt="" className="ml-1 inline h-7 w-7 object-contain align-[-6px] drop-shadow-[0_1px_4px_rgba(0,0,0,.45)]" aria-hidden="true" /> Explora cada rincón: el mapa esconde puntos extra.</p>
                </li>
              </ol>
            </div>

            <section className="mt-4 border-t border-[#F7E04B]/30 pt-4" aria-labelledby="ranking-points-title">
              <p className="text-[10px] font-black uppercase tracking-[.2em] text-[#F7E04B]">Ranking global</p>
              <h3 id="ranking-points-title" className="mt-1 font-display text-2xl uppercase leading-none text-white">
                Puntos que puedes obtener
              </h3>
              <p className="mt-2 text-xs leading-5 text-white/70">
                El puntaje de juego y los puntos oficiales no son lo mismo. El ranking global suma sólo estos premios
                verificados durante la temporada:
              </p>

              <ol className="mt-3 grid gap-2">
                <PointsRule
                  points={[
                    SOLO_PLAY_REWARDS.oneMinute.points,
                    SOLO_PLAY_REWARDS.twoMinutes.points,
                    SOLO_PLAY_REWARDS.victory.points,
                  ].map(pointsLabel).join(' / ')}
                  title="Jugar en solitario"
                  description={[
                    'Sobrevive al menos 1 minuto para recibir 10 puntos, 2 minutos para recibir 20,',
                    'o completa y gana la partida para recibir 30. Se entrega sólo el premio más alto alcanzado.',
                  ].join(' ')}
                  frequency="En cada partida en solitario verificada, sin límite. No requiere GPS."
                />
                <PointsRule
                  points="Tu puntaje de juego"
                  title="Primera visita a una zona"
                  description="Termina una partida con GPS dentro de una zona que todavía no hayas acreditado. Se suma al ranking el puntaje completo de esa partida."
                  frequency="Una vez por cada una de las 5 zonas en cada temporada; máximo 5 premios."
                />
                <PointsRule
                  points={achievementPointsLabel}
                  title="Desbloquear una medalla"
                  description="Cumple la meta de una medalla: partidas, victorias, aciertos, rachas, duelos, zonas o hallazgos ocultos."
                  frequency={`Una vez por cada una de las ${ACHIEVEMENTS.length} medallas en cada temporada.`}
                >
                  <details className="mt-3 border-t border-white/10 pt-2">
                    <summary className="min-h-[44px] cursor-pointer py-3 text-[10px] font-black uppercase tracking-[.12em] text-[#C9A8FF]">
                      Ver las {ACHIEVEMENTS.length} medallas y sus puntos
                    </summary>
                    <ul className="grid gap-2 pb-1">
                      {ACHIEVEMENTS.map((achievement) => (
                        <li key={achievement.id} className="grid grid-cols-[1fr_auto] gap-3 border-t border-white/10 pt-2">
                          <p className="text-[11px] leading-4 text-white/75">
                            <strong className="text-white">{achievement.title}.</strong> {achievement.description}
                          </p>
                          <strong className="text-xs text-[#F7E04B]">
                            {pointsLabel(guide.scoring.achievements?.[achievement.id] ?? 100)}
                          </strong>
                        </li>
                      ))}
                    </ul>
                  </details>
                </PointsRule>
                <PointsRule
                  points={pointsLabel(guide.scoring.challengeWinner)}
                  title="Ganar un reto"
                  description="El premio se acredita cuando las dos personas terminan el duelo y tu puntaje de juego es mayor."
                  frequency="Una vez por rival distinto en cada temporada. Repetir contra la misma persona no vuelve a premiar."
                />
                <PointsRule
                  points={pointsLabel(guide.scoring.challengeLoser)}
                  title="Perder o empatar un reto"
                  description="Completar el duelo también da un premio si pierdes o si ambos obtienen el mismo puntaje."
                  frequency="Una vez por rival distinto en cada temporada. Comparte el mismo límite que el premio por ganar."
                />
                <PointsRule
                  points={pointsLabel(guide.hiddenPoints.pointsPerFind)}
                  title="Encontrar un punto oculto"
                  description="Toca uno de los 24 fantasmas escondidos entre el mapa general y los mapas de zona."
                  frequency="Cada fantasma, una vez por hora. Los 24 reaparecen al comenzar la siguiente hora."
                />
              </ol>

              <div className="mt-3 border border-white/15 bg-white/5 p-3 text-[11px] leading-5 text-white/70">
                <strong className="text-white">No suman directamente al ranking global:</strong> crear o compartir un reto,
                volver a visitar una zona ya acreditada y jugar un torneo. Repetir una zona no repite el premio GPS, pero
                una partida en solitario sí conserva su premio por duración. Los torneos usan su propio ranking.
              </div>
            </section>

            <div className="mt-3 flex gap-2 border-t border-[#7EC54A]/20 pt-3 text-[10px] font-black uppercase tracking-[.14em] text-[#C5E6A0]">
              <span className="rounded-sm border border-[#7EC54A]/40 bg-black/30 px-2 py-1">🎧 Usa audífonos</span>
              <span className="rounded-sm border border-[#7EC54A]/40 bg-black/30 px-2 py-1">📱 Juega en vertical</span>
            </div>
            </div>
          </div>
        </div>,
        document.body,
      )}
    </>
  );
}
